/** Vector representation of a plan's line work (image pixel coordinates). */

export interface Seg {
  id: number;
  pts: number[]; // flat polyline in image px
  len: number; // image px
  thick: number; // average stroke thickness (image px)
  a: number; // node id at start
  b: number; // node id at end
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  /** Its ink's half-width along it, [t, r, …] (t 0 → 1 from a to b; r image px): arrowheads, tapers, fills. */
  prof?: number[];
  /** Part of a leader (a label's line to what it labels, its arrowhead): never line work to snap to. */
  leader?: boolean;
}

export interface Node {
  id: number;
  x: number;
  y: number;
  segs: number[]; // incident segment ids
}

/** Virtual link across a gap (dashed line) between two endpoint nodes. */
export interface Bridge {
  a: number;
  b: number;
  len: number;
}

export interface VectorPlan {
  w: number; // image size
  h: number;
  k: number; // working scale: working px = image px * k
  cw: number; // working raster size
  ch: number;
  segs: Seg[];
  nodes: Node[];
  bridges: Bridge[];
  /** Line-work mask at working resolution (text removed), 1 = ink. */
  lineMask: Uint8Array;
  /** One-pixel-wide skeleton of the line work (centrelines), 1 = skeleton. */
  skel: Uint8Array;
  /** Distance (working px, clamped 0..63) from each pixel to the nearest line ink. */
  edt: Uint8Array;
  /** Text row boxes (image px), for debugging and to keep snapping away from labels. */
  textRows: { minX: number; minY: number; maxX: number; maxY: number }[];
  stats: { ms: number; comps: number; textComps: number; skelPx: number; times?: Record<string, number> };
  /** Luminance at full image resolution (w × h), for placing borders on the ink's edge; null for very large plans. */
  luma: Uint8Array | null;
}

/** What reading along the plan's ink needs of it: its size, working scale and line work. */
export type PlanRaster = Pick<VectorPlan, 'w' | 'h' | 'k' | 'cw' | 'ch' | 'lineMask'>;

/** Wire format for postMessage (typed arrays are transferred). */
export interface PlanWire {
  w: number;
  h: number;
  k: number;
  cw: number;
  ch: number;
  segPts: Float32Array;
  segOffsets: Uint32Array; // n+1 offsets into segPts (in floats)
  segMeta: Float32Array; // per seg: len, thick, a, b (4 floats)
  segProf: Float32Array; // per seg: its width profile, [t, r] pairs
  segProfOffsets: Uint32Array; // n+1 offsets into segProf (in floats)
  nodeXY: Float32Array;
  bridges: Float32Array; // a, b, len triples
  lineMask: Uint8Array;
  skel: Uint8Array;
  edt: Uint8Array;
  luma: Uint8Array | null;
  textRows: Float32Array; // quads
  stats: VectorPlan['stats'];
}

export function toWire(p: VectorPlan): { wire: PlanWire; transfer: ArrayBuffer[] } {
  const total = p.segs.reduce((n, s) => n + s.pts.length, 0);
  const segPts = new Float32Array(total);
  const segOffsets = new Uint32Array(p.segs.length + 1);
  const segMeta = new Float32Array(p.segs.length * 4);
  let o = 0;
  p.segs.forEach((s, i) => {
    segOffsets[i] = o;
    segPts.set(s.pts, o);
    o += s.pts.length;
    segMeta[i * 4] = s.len;
    segMeta[i * 4 + 1] = s.thick;
    segMeta[i * 4 + 2] = s.a;
    segMeta[i * 4 + 3] = s.b;
  });
  segOffsets[p.segs.length] = o;
  const segProfOffsets = new Uint32Array(p.segs.length + 1);
  const segProf = new Float32Array(p.segs.reduce((n, s) => n + (s.prof?.length ?? 0), 0));
  let po = 0;
  p.segs.forEach((s, i) => {
    segProfOffsets[i] = po;
    if (s.prof) segProf.set(s.prof, po);
    po += s.prof?.length ?? 0;
  });
  segProfOffsets[p.segs.length] = po;
  const nodeXY = new Float32Array(p.nodes.length * 2);
  p.nodes.forEach((n, i) => {
    nodeXY[i * 2] = n.x;
    nodeXY[i * 2 + 1] = n.y;
  });
  const bridges = new Float32Array(p.bridges.length * 3);
  p.bridges.forEach((b, i) => {
    bridges[i * 3] = b.a;
    bridges[i * 3 + 1] = b.b;
    bridges[i * 3 + 2] = b.len;
  });
  const textRows = new Float32Array(p.textRows.length * 4);
  p.textRows.forEach((r, i) => textRows.set([r.minX, r.minY, r.maxX, r.maxY], i * 4));
  const wire: PlanWire = { w: p.w, h: p.h, k: p.k, cw: p.cw, ch: p.ch, segPts, segOffsets, segMeta, segProf, segProfOffsets, nodeXY, bridges, lineMask: p.lineMask, skel: p.skel, edt: p.edt, luma: p.luma, textRows, stats: p.stats };
  const transfer = [segPts.buffer, segOffsets.buffer, segMeta.buffer, segProf.buffer, segProfOffsets.buffer, nodeXY.buffer, bridges.buffer, p.lineMask.buffer as ArrayBuffer, p.skel.buffer as ArrayBuffer, p.edt.buffer as ArrayBuffer, textRows.buffer];
  if (p.luma) transfer.push(p.luma.buffer as ArrayBuffer);
  return { wire, transfer };
}

export function fromWire(w: PlanWire): VectorPlan {
  const segs: Seg[] = [];
  const n = w.segOffsets.length - 1;
  for (let i = 0; i < n; i++) {
    const pts = Array.from(w.segPts.subarray(w.segOffsets[i]!, w.segOffsets[i + 1]!));
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (let j = 0; j < pts.length; j += 2) {
      const x = pts[j]!,
        y = pts[j + 1]!;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    const prof = w.segProf && w.segProfOffsets ? Array.from(w.segProf.subarray(w.segProfOffsets[i]!, w.segProfOffsets[i + 1]!)) : [];
    segs.push({ id: i, pts, len: w.segMeta[i * 4]!, thick: w.segMeta[i * 4 + 1]!, a: w.segMeta[i * 4 + 2]!, b: w.segMeta[i * 4 + 3]!, minX, minY, maxX, maxY, ...(prof.length ? { prof } : {}) });
  }
  const nodes: Node[] = [];
  for (let i = 0; i < w.nodeXY.length / 2; i++) nodes.push({ id: i, x: w.nodeXY[i * 2]!, y: w.nodeXY[i * 2 + 1]!, segs: [] });
  for (const s of segs) {
    nodes[s.a]?.segs.push(s.id);
    if (s.b !== s.a) nodes[s.b]?.segs.push(s.id);
  }
  const bridges: Bridge[] = [];
  for (let i = 0; i < w.bridges.length / 3; i++) bridges.push({ a: w.bridges[i * 3]!, b: w.bridges[i * 3 + 1]!, len: w.bridges[i * 3 + 2]! });
  const textRows = [];
  for (let i = 0; i < w.textRows.length / 4; i++) textRows.push({ minX: w.textRows[i * 4]!, minY: w.textRows[i * 4 + 1]!, maxX: w.textRows[i * 4 + 2]!, maxY: w.textRows[i * 4 + 3]! });
  return { w: w.w, h: w.h, k: w.k, cw: w.cw, ch: w.ch, segs, nodes, bridges, lineMask: w.lineMask, skel: w.skel, edt: w.edt, luma: w.luma ?? null, textRows, stats: w.stats };
}
